import { CaptchaActions } from '@/constants';
import { getDateString } from '@/utils/dates';
import axios from '@/utils/axios';

const handleAuthResult = ({
	applicantId = 0,
	applicationId = 0,
	loanApplicationId = 0,
	firstName = '',
	lastName = '',
	authenticationResult = 0,
	captchaAction = CaptchaActions.NONE,
	hasDuplicateApplication = false,
	isEligibleToUseOLA = false,
	applicationToken = '',
	applicantToken = '',
	accessToken = '',
	accessTokenExpiresUtc = null
}) => {
	switch (authenticationResult) {
		case 0:
			if (captchaAction === CaptchaActions.NONE) {
				throw 'There was an error verifying login credentials.';
			}
			break;
		case 1:
			// Success, let's return the applicant info
			break;
		case 2:
		case 3:
			throw 'The provided user name and password combination is incorrect.';
		case 4:
			throw 'The account provided is locked.';
		case 5:
			throw 'The account provided requires a password change.';
		default:
			if (captchaAction === CaptchaActions.NONE) {
				throw 'There was an error verifying login credentials.';
			}
	}

	return {
		applicantId,
		applicationId,
		loanApplicationId,
		captchaAction,
		firstName,
		lastName,
		hasDuplicateApplication,
		isEligibleToUseOLA,
		applicationToken,
		applicantToken,
		accessToken,
		accessTokenExpiresUtc
	};
};

const authenticateApplicant = async request => {
	try {
		const { data } = await axios.post(`${process.env.BASE_URL}api/Authenticate`, {
			...request,
			loadUrl: window.location.href
		});

		return handleAuthResult(data);
	} catch (error) {
		throw error;
	}
};

const authenticateSSO = async request => {
	try {
		const { data } = await axios.post(`${process.env.BASE_URL}api/SSO`, {
			...request,
			loadUrl: window.location.href
		});

		return data;
	} catch (error) {
		throw error;
	}
};

const getApplicant = async applicantToken => {
	try {
		const {
			data: { applicant }
		} = await axios.get(`${process.env.BASE_URL}api/Applicant/${encodeURIComponent(applicantToken)}`);

		return {
			...applicant,
			dateOfBirth: applicant.dateOfBirth ? getDateString(applicant.dateOfBirth) : ''
		};
	} catch (error) {
		throw error;
	}
};

const createApplicant = async request => {
	try {
		const { data } = await axios.post(`${process.env.BASE_URL}api/Applicant`, {
			...request,
			loadUrl: window.location.href
		});

		return data;
	} catch (error) {
		throw error;
	}
};

const updateApplicant = async ({ applicationId, applicant }) => {
	try {
		const {
			data: { updated, emailVerificationResult }
		} = await axios.post(`${process.env.BASE_URL}api/Applicant/Update`, {
			applicationId,
			applicant
		});

		if (!updated && emailVerificationResult.emailIsValid) {
			throw 'There was an error updating the Co-Applicant record';
		}

		return {
			applicant: applicant,
			emailVerificationResult: emailVerificationResult
		};
	} catch (error) {
		throw error;
	}
};

const authenticateSamlSSO = async request => {
	try {
		const { data } = await axios.post(`${process.env.BASE_URL}api/SamlSSO`, {
			...request,
			loadUrl: window.location.href
		});

		return data;
	} catch (error) {
		throw error;
	}
};

const getSmsOptInPageConfig = async (applicationToken) => {
	try {

		const {
			data: { smsOptInLabel, preselectOptIn }
		} = await axios.get(`${process.env.BASE_URL}api/Page/SmsOptIn/Configuration/${applicationToken}`);

		return { smsOptInLabel, preselectOptIn };
	} catch (error) {
		throw error;
	}
};

const updateApplicantSmsOptIn = async ({ applicantId, optedInForSms }) => {
	try {
		const {
			data: { updated }
		} = await axios.post(`${process.env.BASE_URL}api/Applicant/SmsOptIn/Update`, {
			applicantId,
			optedInForSms
		});

		if (!updated) {
			throw 'There was an error updating the applicant SMS Opt In record';
		}

		return updated;
	} catch (error) {
		throw error;
	}
};

export default {
	authenticateApplicant,
	getApplicant,
	createApplicant,
	updateApplicant,
	authenticateSSO,
	authenticateSamlSSO,
	getSmsOptInPageConfig,
	updateApplicantSmsOptIn,
};

export { authenticateApplicant, getApplicant, createApplicant, updateApplicant, authenticateSSO, authenticateSamlSSO, getSmsOptInPageConfig };
